import { ScrollArea } from "@base-ui/react"
import React, { useContext, useMemo } from "react"
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  Resolver,
  useForm,
  useFormContext
} from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { OverlayIDContext } from "shared/context/overlay/OverlayIDContext"
import { OverlayStyle } from "shared/context/overlay/types"
import { Button, ButtonProps, ButtonVariant } from "shared/ui/buttons/Button"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Checkbox } from "shared/ui/input/select/checkbox/Checkbox"
import { TextInput } from "shared/ui/input/text/TextInput"
import { OverlayWrapper } from "shared/ui/overlay/OverlayWrapper"

import type { ConfirmationOverlayPayload } from "./DefaultConfirmationOverlay"
import {
  ConfirmationOverlayContext,
  useConfirmationOverlayContext
} from "./lib/ConfirmationOverlayContext"
import {
  ConfirmationOverlayFormContext,
  ConfirmationOverlayFormContextValue,
  useConfirmationOverlayFormContext
} from "./lib/ConfirmationOverlayFormContext"
import "./styles/_confirmation-overlay.scss"

export interface ConfirmationOverlayRootProps {
  id: string
  isDanger?: boolean
  isLoading?: boolean
  autoFocus?: boolean
  children: React.ReactNode

  className?: string
  classNamePrefix?: string
}

export interface ConfirmationOverlayHeaderProps {
  children: React.ReactNode
  className?: string
}

export interface ConfirmationOverlayMessageProps {
  children: React.ReactNode
  className?: string
}

export interface ConfirmationOverlayContentProps {
  children: React.ReactNode
  className?: string
}

export interface ConfirmationOverlayFormProps<
  TFormData extends FieldValues = FieldValues,
  TTransformedData extends FieldValues = TFormData
> {
  id?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolver?: Resolver<TFormData, any, TTransformedData>
  defaultValues?: DefaultValues<TFormData>
  onSubmit: (data: TTransformedData) => void
  children: React.ReactNode
}

export interface ConfirmationOverlayTextConfirmationProps {
  name: string
  label?: string
  placeholder?: string
  autoFocus?: boolean
  className?: string
}

export interface ConfirmationOverlayCheckboxConfirmationProps {
  name: string
  label: React.ReactNode
  className?: string
  disabled?: boolean
}

export interface ConfirmationOverlayActionsProps {
  children: React.ReactNode
  className?: string
}

export interface ConfirmationOverlayCancelProps extends Partial<ButtonProps> {
  children?: React.ReactNode
  onClick?: () => void
}

export interface ConfirmationOverlayCloseProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  onClose: () => void
}

const Root = React.memo(
  ({
    id,
    isDanger = false,
    isLoading = false,
    autoFocus = true,
    className,
    classNamePrefix,
    children
  }: ConfirmationOverlayRootProps) => {
    const contextValue = useMemo(
      () => ({ id, isDanger, isLoading, autoFocus }),
      [id, isDanger, isLoading, autoFocus]
    )

    return (
      <ConfirmationOverlayContext.Provider value={contextValue}>
        <OverlayWrapper
          id={id}
          overlayStyle={OverlayStyle.CONFIRMATION}
          className={className}
          classNamePrefix={classNamePrefix}
        >
          {children}
        </OverlayWrapper>
      </ConfirmationOverlayContext.Provider>
    )
  }
)
Root.displayName = "ConfirmationOverlay.Root"

const Header = React.memo(
  ({ children, className }: ConfirmationOverlayHeaderProps) => {
    return (
      <div className={classNames("confirmation-overlay__header", className)}>
        {children}
      </div>
    )
  }
)
Header.displayName = "ConfirmationOverlay.Header"

const Title = React.memo(
  ({ children, className }: ConfirmationOverlayHeaderProps) => {
    const overlayID = useContext(OverlayIDContext)

    return (
      <h2
        id={overlayID ? `${overlayID}-title` : undefined}
        className={classNames("confirmation-overlay__title", className)}
      >
        {children}
      </h2>
    )
  }
)
Title.displayName = "ConfirmationOverlay.Title"

const Message = React.memo(
  ({ children, className }: ConfirmationOverlayMessageProps) => {
    return (
      <div className={classNames("confirmation-overlay__message", className)}>
        {children}
      </div>
    )
  }
)
Message.displayName = "ConfirmationOverlay.Message"

const Content = React.memo(
  ({ children, className }: ConfirmationOverlayContentProps) => {
    return (
      <ScrollArea.Root
        className={classNames(
          "confirmation-overlay__content",
          "scroll-area",
          "scroll-area--vertical",
          className
        )}
      >
        <ScrollArea.Viewport className="scroll-area__viewport">
          <ScrollArea.Content className="scroll-area__content">
            {children}
          </ScrollArea.Content>
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar
          orientation="vertical"
          className="scroll-area__scrollbar"
        >
          <ScrollArea.Thumb className="scroll-area__thumb" />
        </ScrollArea.Scrollbar>
      </ScrollArea.Root>
    )
  }
)
Message.displayName = "ConfirmationOverlay.Content"

// eslint-disable-next-line react/function-component-definition
const FormComponent = <
  TFormData extends FieldValues = FieldValues,
  TTransformedData extends FieldValues = TFormData
>({
  id,
  resolver,
  defaultValues,
  onSubmit,
  children
}: ConfirmationOverlayFormProps<TFormData, TTransformedData>) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const form = useForm<TFormData, any, TTransformedData>({
    resolver,
    defaultValues,
    mode: "onChange"
  })

  const handleSubmit = form.handleSubmit(onSubmit)

  const formContextValue = useMemo<ConfirmationOverlayFormContextValue>(
    () => ({
      isSubmitDisabled: resolver ? !form.formState.isValid : false,
      isSubmitting: form.formState.isSubmitting
    }),
    [resolver, form.formState.isValid, form.formState.isSubmitting]
  )

  return (
    <ConfirmationOverlayFormContext.Provider value={formContextValue}>
      <FormProvider {...form}>
        <form
          id={id}
          onSubmit={handleSubmit}
          className="confirmation-overlay__form"
        >
          {children}
        </form>
      </FormProvider>
    </ConfirmationOverlayFormContext.Provider>
  )
}
FormComponent.displayName = "ConfirmationOverlay.Form"

const TextConfirmation = React.memo(
  ({
    name,
    label,
    placeholder,
    autoFocus = true,
    className
  }: ConfirmationOverlayTextConfirmationProps) => {
    return (
      <TextInput.Root
        name={name}
        label={label}
        className={classNames(
          "confirmation-overlay__text-confirmation",
          className
        )}
      >
        <TextInput.Label />
        <TextInput.Focus>
          <TextInput.Input
            placeholder={placeholder}
            autoFocus={autoFocus}
            data-testid="confirmation-overlay__text-input"
          />
        </TextInput.Focus>
        <TextInput.ErrorMessage />
      </TextInput.Root>
    )
  }
)
TextConfirmation.displayName = "ConfirmationOverlay.TextConfirmation"

const CheckboxConfirmation = React.memo(
  ({
    name,
    label,
    className,
    disabled
  }: ConfirmationOverlayCheckboxConfirmationProps) => {
    const { watch, setValue } = useFormContext()

    return (
      <div className="confirmation-overlay__checkbox-confirmation">
        <Checkbox
          name={name}
          value={watch(name)}
          onChange={value =>
            setValue(name, value, {
              shouldValidate: true
            })
          }
          label={label}
          className={className}
          disabled={disabled}
          data-testid="confirmation-overlay__checkbox-input"
        />
      </div>
    )
  }
)
CheckboxConfirmation.displayName = "ConfirmationOverlay.CheckboxConfirmation"

const Actions = React.memo(
  ({ children, className }: ConfirmationOverlayActionsProps) => {
    return (
      <div className={classNames("confirmation-overlay__actions", className)}>
        {children}
      </div>
    )
  }
)
Actions.displayName = "ConfirmationOverlay.Actions"

const Submit = React.memo(
  ({ children, form, onClick, disabled, ...props }: ButtonProps) => {
    const { id, isDanger, isLoading } = useConfirmationOverlayContext()
    const { isSubmitDisabled, isSubmitting } =
      useConfirmationOverlayFormContext()
    const { t } = useTranslation()

    return (
      <Button
        id={`${id}__confirmation-overlay__confirm`}
        type={form ? "submit" : "button"}
        isDanger={isDanger}
        isLoading={isLoading || isSubmitting}
        variant={ButtonVariant.PRIMARY}
        className="confirmation-overlay__action"
        form={form}
        disabled={disabled || isSubmitDisabled}
        onClick={onClick}
        data-testid="confirmation-overlay__confirm"
        {...props}
      >
        {children || t("General.Confirm")}
      </Button>
    )
  }
)
Submit.displayName = "ConfirmationOverlay.Submit"

const Cancel = React.memo(
  ({ children, onClick, ...props }: ConfirmationOverlayCancelProps) => {
    const { id, autoFocus } = useConfirmationOverlayContext()
    const { t } = useTranslation()

    return (
      <Button
        id={`${id}__confirmation-overlay__cancel`}
        variant={ButtonVariant.TERTIARY}
        className="confirmation-overlay__action"
        autoFocus={autoFocus}
        onClick={onClick}
        data-testid="confirmation-overlay__cancel"
        {...props}
      >
        {children || t("General.Cancel")}
      </Button>
    )
  }
)
Cancel.displayName = "ConfirmationOverlay.Cancel"

const Close = React.memo(
  ({ onClose, ...props }: ConfirmationOverlayCloseProps) => {
    const { t } = useTranslation()

    return (
      <Button
        variant={ButtonVariant.TERTIARY}
        className={classNames("overlay__close", "btn--bleed")}
        onClick={onClose}
        aria-label={t("General.Cancel")}
        isIconOnly
        icon={IconName.CLOSE}
        {...props}
      />
    )
  }
)
Close.displayName = "ConfirmationOverlay.Close"

const Form = FormComponent as <
  TFormData extends FieldValues = FieldValues,
  TTransformedData extends FieldValues = TFormData
>(
  props: ConfirmationOverlayFormProps<TFormData, TTransformedData>
) => React.ReactElement

export const ConfirmationOverlay = {
  Root,
  Header,
  Title,
  Message,
  Form,
  TextConfirmation,
  CheckboxConfirmation,
  Content,
  Actions,
  Submit,
  Cancel,
  Close
}

declare module "shared/context/overlay/registry" {
  interface OverlayPayloadRegistry {
    CONFIRMATION_OVERLAY: ConfirmationOverlayPayload
  }
}
